import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { Link } from 'react-router';

import type { Ticket, TicketCreate, TicketDetail, TicketPriority, TicketStatus } from '../api/types';
import { useMe } from '../auth/context';
import { Icon } from '../components/icons';
import { MutationError } from '../components/MutationError';
import { useProject } from '../projects/context';
import { projectKeys, useProjectApi } from '../projects/useProjectApi';
import { fmtDateTime } from './format';
import { RichText } from './RichText';
import { ago, initials } from './ticketView';
import { assigneeLabel, isOpen, localInputToIso, needsResult, priorityClass, teamLabels, ticketEventLabels, ticketPriorityLabels, ticketStatusLabels } from './tickets';

interface Context {
  leadId?: string;
  appointmentId?: string;
  callId?: string;
}

// useTicketMeta: Kategorien und Projektmitglieder für Formulare (gecacht).
function useTicketMeta(enabled: boolean) {
  const { project, can } = useProject();
  const papi = useProjectApi();
  const categories = useQuery({
    queryKey: projectKeys.ticketCategories(project.id),
    queryFn: ({ signal }) => papi.ticketCategories(signal),
    staleTime: 5 * 60_000,
    enabled,
  });
  const members = useQuery({
    queryKey: projectKeys.members(project.id),
    queryFn: ({ signal }) => papi.members(signal),
    staleTime: 5 * 60_000,
    enabled: enabled && can('project.read'),
  });
  const people = new Map<string, string>();
  for (const m of members.data?.items ?? []) people.set(m.user_id, `${m.first_name} ${m.last_name}`.trim());
  return { categories: (categories.data?.items ?? []).filter((c) => c.is_active), people };
}

// TicketCreateForm: neue Rückfrage oder Aufgabe – bewusst wenige Felder.
export function TicketCreateForm({
  kind,
  context,
  onDone,
  onCancel,
}: {
  kind: 'REQUEST' | 'TASK';
  context: Context;
  onDone: (t: Ticket) => void;
  onCancel: () => void;
}) {
  const papi = useProjectApi();
  const { categories, people } = useTicketMeta(true);
  const [form, setForm] = useState({ subject: '', description: '', category: '', assign: '', priority: 'NORMAL' as TicketPriority, due: '' });
  const create = useMutation({
    mutationFn: () => {
      const body: TicketCreate = {
        kind,
        subject: form.subject.trim(),
        description: form.description.trim(),
        priority: form.priority,
        category: form.category || 'GENERAL',
      };
      if (form.assign.startsWith('role:')) body.assigned_role = form.assign.slice(5) as TicketCreate['assigned_role'];
      else if (form.assign.startsWith('user:')) body.assigned_to_user_id = form.assign.slice(5);
      if (context.leadId) body.lead_id = context.leadId;
      if (context.appointmentId) body.appointment_id = context.appointmentId;
      if (context.callId) body.call_id = context.callId;
      const due = localInputToIso(form.due);
      if (due) body.due_at = due;
      return papi.createTicket(body);
    },
    onSuccess: onDone,
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    create.mutate();
  };
  return (
    <form className="card inline-panel ticket-form" onSubmit={submit} aria-label={kind === 'TASK' ? 'Neue Aufgabe' : 'Neue Rückfrage'}>
      <label className="field wide">
        <span>{kind === 'TASK' ? 'Aufgabe' : 'Worum geht es?'}</span>
        <input
          value={form.subject}
          onChange={(e) => setForm({ ...form, subject: e.target.value })}
          placeholder={kind === 'TASK' ? 'z. B. Kunde morgen zurückrufen' : 'z. B. Bitte Terminadresse prüfen'}
          maxLength={200}
          required
        />
      </label>
      <label className="field wide">
        <span>Details (optional)</span>
        <textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} maxLength={10000} />
      </label>
      <div className="form-grid">
        <label className="field">
          <span>Kategorie</span>
          <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
            <option value="">Allgemein</option>
            {categories
              .filter((c) => c.key !== 'GENERAL')
              .map((c) => (
                <option key={c.id} value={c.key}>
                  {c.name}
                </option>
              ))}
          </select>
        </label>
        <label className="field">
          <span>Zuständig</span>
          <select value={form.assign} onChange={(e) => setForm({ ...form, assign: e.target.value })}>
            <option value="">Team der Kategorie</option>
            <optgroup label="Team">
              {Object.entries(teamLabels).map(([key, label]) => (
                <option key={key} value={`role:${key}`}>
                  {label}
                </option>
              ))}
            </optgroup>
            {people.size > 0 && (
              <optgroup label="Person">
                {[...people].map(([uid, name]) => (
                  <option key={uid} value={`user:${uid}`}>
                    {name}
                  </option>
                ))}
              </optgroup>
            )}
          </select>
        </label>
        <label className="field narrow">
          <span>Priorität</span>
          <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value as TicketPriority })}>
            {(Object.keys(ticketPriorityLabels) as TicketPriority[]).map((p) => (
              <option key={p} value={p}>
                {ticketPriorityLabels[p]}
              </option>
            ))}
          </select>
        </label>
        <label className="field narrow">
          <span>Fällig</span>
          <input type="datetime-local" value={form.due} onChange={(e) => setForm({ ...form, due: e.target.value })} />
        </label>
      </div>
      {create.isError && <MutationError error={create.error} />}
      <div className="form-actions">
        <button type="submit" className="btn btn-primary" disabled={create.isPending}>
          {kind === 'TASK' ? 'Aufgabe anlegen' : 'Rückfrage senden'}
        </button>
        <button type="button" className="btn btn-ghost" onClick={onCancel}>
          Abbrechen
        </button>
      </div>
    </form>
  );
}

// useTicketUpdate: Status und Zuständigkeit ändern (Übernehmen, Wartet, Erledigen, erneut öffnen).
function useTicketUpdate(ticketId: string) {
  const { project } = useProject();
  const papi = useProjectApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { status?: TicketStatus; assigned_to_user_id?: string; note?: string }) => papi.updateTicket(ticketId, body),
    onSuccess: (t: TicketDetail | Ticket) => {
      if ('comments' in t) queryClient.setQueryData(projectKeys.ticket(project.id, ticketId), t);
      void queryClient.invalidateQueries({ queryKey: projectKeys.all(project.id) });
    },
  });
}

// TicketActions: die Arbeitsschritte eines Vorgangs – auf der Detailseite oben im Kopf, am Lead unter dem Verlauf.
export function TicketActions({ t, small = false }: { t: TicketDetail; small?: boolean }) {
  const me = useMe();
  const update = useTicketUpdate(t.id);
  const [closing, setClosing] = useState(false);
  const [result, setResult] = useState('');
  if (!t.can_work) return null;
  const mine = t.assigned_to_user_id === me.user.id;
  const btn = small ? 'btn btn-small' : 'btn';
  return (
    <div className="form-actions ticket-actions">
      {isOpen(t.status) && !mine && (
        <button type="button" className={btn} disabled={update.isPending} onClick={() => update.mutate({ assigned_to_user_id: me.user.id, status: 'IN_PROGRESS' })}>
          Übernehmen
        </button>
      )}
      {isOpen(t.status) && t.status !== 'WAITING' && (
        <button type="button" className={`${btn} btn-ghost`} disabled={update.isPending} onClick={() => update.mutate({ status: 'WAITING' })}>
          Wartet auf Antwort
        </button>
      )}
      {isOpen(t.status) && closing ? (
        <form
          className="inline-form ticket-close"
          aria-label="Rückfrage erledigen"
          onSubmit={(e) => {
            e.preventDefault();
            update.mutate({ status: 'DONE', note: result.trim() }, { onSuccess: () => setClosing(false) });
          }}
        >
          <label className="field">
            <span>Ergebnis (steht in der Rückfrage, der Fragende bekommt es)</span>
            <textarea rows={2} maxLength={10000} value={result} onChange={(e) => setResult(e.target.value)} required />
          </label>
          <div className="form-actions">
            <button type="submit" className={`${btn} btn-primary`} disabled={update.isPending || !result.trim()}>
              <Icon name="check" size={16} /> Mit Ergebnis erledigen
            </button>
            <button type="button" className={`${btn} btn-ghost`} onClick={() => setClosing(false)}>
              Abbrechen
            </button>
          </div>
        </form>
      ) : isOpen(t.status) ? (
        <button
          type="button"
          className={`${btn} btn-primary`}
          disabled={update.isPending}
          onClick={() => (needsResult(t) ? setClosing(true) : update.mutate({ status: 'DONE' }))}
        >
          <Icon name="check" size={16} /> Erledigen
        </button>
      ) : (
        <button type="button" className={btn} disabled={update.isPending} onClick={() => update.mutate({ status: 'OPEN' })}>
          Erneut öffnen
        </button>
      )}
      {update.isError && <MutationError error={update.error} />}
    </div>
  );
}

// TicketThread: Beschreibung, Unterhaltung (Kommentare und E-Mails), Verlauf und – am Lead – die Arbeitsschritte.
// Auf der Detailseite stehen Beschreibung und Arbeitsschritte eigenständig (withDescription/withActions = false).
export function TicketThread({
  ticketId,
  showHistory = false,
  withDescription = true,
  withActions = true,
}: {
  ticketId: string;
  showHistory?: boolean;
  withDescription?: boolean;
  withActions?: boolean;
}) {
  const { project } = useProject();
  const papi = useProjectApi();
  const queryClient = useQueryClient();
  const detail = useQuery({ queryKey: projectKeys.ticket(project.id, ticketId), queryFn: ({ signal }) => papi.ticket(ticketId, signal) });
  const [comment, setComment] = useState('');
  const addComment = useMutation({
    mutationFn: () => papi.addTicketComment(ticketId, comment.trim()),
    onSuccess: () => {
      setComment('');
      void queryClient.invalidateQueries({ queryKey: projectKeys.all(project.id) });
    },
  });
  if (detail.isPending) return <p className="muted">Lädt …</p>;
  if (detail.isError) return <p className="muted">Die Rückfrage konnte nicht geladen werden.</p>;
  const t = detail.data;
  return (
    <div className="ticket-thread">
      {withDescription && t.description && <RichText text={t.description} />}
      {t.comments.length > 0 ? (
        <ol className="conversation" aria-label="Kommentare">
          {t.comments.map((c) => {
            const name = c.user_name ?? (c.source === 'EMAIL' ? 'E-Mail' : 'System');
            const from = c.source === 'EMAIL' ? 'from-email' : c.user_name ? 'from-person' : 'from-system';
            return (
              <li key={c.id} className={`message ${from}`}>
                <span className="avatar" aria-hidden="true">
                  {c.source === 'EMAIL' ? <Icon name="mail" size={13} /> : c.user_name ? initials(c.user_name) : <Icon name="sparkle" size={13} />}
                </span>
                <div className="message-bubble">
                  <div className="message-meta">
                    <strong>{name}</strong>
                    <time className="muted" dateTime={c.created_at} title={fmtDateTime(c.created_at)}>
                      {ago(c.created_at)}
                    </time>
                    {c.edited_at && <span className="muted">(bearbeitet)</span>}
                  </div>
                  <RichText text={c.body} compact />
                </div>
              </li>
            );
          })}
        </ol>
      ) : (
        !withDescription && <p className="muted conversation-empty">Noch keine Kommentare.</p>
      )}
      {t.can_comment && (
        <form
          className="comment-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (comment.trim()) addComment.mutate();
          }}
        >
          <label className="field wide">
            <span className="visually-hidden">Kommentar</span>
            <textarea rows={2} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Antwort oder Hinweis …" maxLength={10000} />
          </label>
          <button type="submit" className="btn btn-small" disabled={addComment.isPending || !comment.trim()}>
            Kommentieren
          </button>
        </form>
      )}
      {showHistory && t.events.length > 0 && (
        <details className="ticket-history">
          <summary>Verlauf ({t.events.length})</summary>
          <ol className="plain-list">
            {t.events.map((e) => (
              <li key={e.id}>
                <time className="muted">{fmtDateTime(e.created_at)}</time> {e.actor_name ?? 'System'}: {ticketEventLabels[e.type] ?? e.type}
              </li>
            ))}
          </ol>
        </details>
      )}
      {withActions && <TicketActions t={t} small />}
      {addComment.isError && <MutationError error={addComment.error} />}
    </div>
  );
}

export function TicketSummary({ t }: { t: Ticket }) {
  return (
    <>
      <span className="ticket-subject">
        <strong>{t.subject}</strong>
        <span className="muted">
          {' '}
          · {t.reference} · {assigneeLabel(t)}
          {t.due_at && ` · fällig ${fmtDateTime(t.due_at)}`}
        </span>
      </span>
      <span className="badges">
        {t.priority !== 'NORMAL' && <span className={priorityClass(t.priority)}>{ticketPriorityLabels[t.priority]}</span>}
        <span className={isOpen(t.status) ? 'badge' : 'badge badge-muted'}>{ticketStatusLabels[t.status]}</span>
        {t.overdue && <span className="badge badge-error">überfällig</span>}
        {t.comment_count > 0 && (
          <span className="badge badge-muted" title="Kommentare">
            <Icon name="message" size={13} /> {t.comment_count}
            <span className="visually-hidden"> Kommentare</span>
          </span>
        )}
      </span>
    </>
  );
}

// TicketPanel: „Rückfragen & Aufgaben“ direkt am Lead oder Termin – ohne Wechsel in ein anderes System.
export function TicketPanel({
  leadId,
  appointmentId,
  title = 'Rückfragen & Aufgaben',
  hideWhenEmpty = false,
}: Context & { title?: string; hideWhenEmpty?: boolean }) {
  const { project, can } = useProject();
  const papi = useProjectApi();
  const queryClient = useQueryClient();
  const q = { lead_id: appointmentId ? undefined : leadId, appointment_id: appointmentId, limit: 30 };
  const list = useQuery({ queryKey: projectKeys.tickets(project.id, q), queryFn: ({ signal }) => papi.tickets(q, signal), enabled: can('ticket.read') });
  const [creating, setCreating] = useState<'' | 'REQUEST' | 'TASK'>('');
  const [open, setOpen] = useState<string>('');
  if (!can('ticket.read')) return null;
  const items = [...(list.data?.items ?? [])].sort((a, b) => Number(isOpen(b.status)) - Number(isOpen(a.status)));
  if (hideWhenEmpty && !creating && !open && (list.isPending || items.length === 0)) return null;
  return (
    <section className="card ticket-panel" aria-labelledby={`tickets-${leadId ?? appointmentId}`}>
      <div className="section-head">
        <h2 id={`tickets-${leadId ?? appointmentId}`}>{title}</h2>
        {can('ticket.create') && !creating && (
          <div className="form-actions">
            <button type="button" className="btn btn-small" onClick={() => setCreating('REQUEST')}>
              Neue Rückfrage
            </button>
            <button type="button" className="btn btn-small btn-ghost" onClick={() => setCreating('TASK')}>
              Neue Aufgabe
            </button>
          </div>
        )}
      </div>
      {creating && (
        <TicketCreateForm
          kind={creating}
          context={{ leadId, appointmentId }}
          onCancel={() => setCreating('')}
          onDone={(t) => {
            setCreating('');
            setOpen(t.id);
            void queryClient.invalidateQueries({ queryKey: projectKeys.all(project.id) });
          }}
        />
      )}
      {list.isPending ? (
        <p className="muted">Lädt …</p>
      ) : list.isError ? (
        <p className="muted">Rückfragen konnten nicht geladen werden.</p>
      ) : items.length === 0 ? (
        <p className="muted">Keine Rückfragen oder Aufgaben.</p>
      ) : (
        <ul className="ticket-list">
          {items.map((t) => (
            <li key={t.id} className={t.priority === 'URGENT' && isOpen(t.status) ? 'ticket-item urgent' : 'ticket-item'}>
              <button type="button" className="ticket-toggle" aria-expanded={open === t.id} onClick={() => setOpen(open === t.id ? '' : t.id)}>
                <TicketSummary t={t} />
              </button>
              {open === t.id && (
                <>
                  <TicketThread ticketId={t.id} />
                  <Link className="small-link" to={`/p/${project.key}/tickets/${t.id}`}>
                    Vollständig öffnen
                  </Link>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
